import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { createApiClient } from "@/config/createApiClient";
import { selectedClientServiceIdAtom } from "@/store";

import { ModificationRequestApi } from "../../config/apiClient";

import { useWithTrackingMutation } from "./useWithTrackingMutation";

const apiClient = createApiClient(ModificationRequestApi);

export const useModificationRequest = () => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const queryClient = useQueryClient();

  const useAddModificationRequest = (params: {
    orderId: string;
    orderItemId: string;
  }) =>
    useWithTrackingMutation(
      (
        body: Parameters<
          typeof apiClient.modificationRequestsControllerCreateModificationRequest
        >["0"]["modificationRequestCreateCommand"]
      ) =>
        apiClient.modificationRequestsControllerCreateModificationRequest({
          clientServiceId: selectedClientServiceIdAtomValue,
          modificationRequestCreateCommand: body,
        }),
      [
        {
          name: "edit_validated_order",
          getMetaData: () => ({
            order_id: params.orderId,
          }),
        },
      ],
      {
        onSuccess: () =>
          Promise.all([
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.refetchQueries({
              queryKey: ["orderItem", params.orderId, params.orderItemId],
            }),
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
            queryClient.invalidateQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
          ]),
      }
    );

  const useCancelModificationRequest = () =>
    useMutation({
      mutationFn: (params: {
        modificationRequestId: string;
        orderId: string;
        orderItemId: string;
      }) =>
        apiClient.modificationRequestsControllerCancelModificationRequest({
          clientServiceId: selectedClientServiceIdAtomValue,
          modificationRequestId: params.modificationRequestId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]),
    });

  return {
    useAddModificationRequest,
    useCancelModificationRequest,
  };
};
